import React, { useState, useEffect, useContext } from "react";

import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import { 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select, 
  Typography, 
  Box, 
  Paper,
  Chip,
  Link,
  FormHelperText
} from "@material-ui/core";
import { 
  Visibility, 
  VisibilityOff, 
  Memory, 
  Android, 
  Security, 
  Settings,
  OpenInNew,
  AttachFile,
  Link as LinkIcon,
  CloudUpload,
  Fullscreen,
  FullscreenExit,
  Layers,
  Description
} from "@material-ui/icons";
import { InputAdornment, IconButton } from "@material-ui/core";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import PromptMediaPreview from "../PromptMediaPreview";
import PromptLayersManager from "../PromptLayersManager";
import PromptDocumentsManager from "../PromptDocumentsManager";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    
    // ✅ ESTILOS PARA EL MODAL PROFESIONAL
    dialogTitle: {
        background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
        color: "white",
        padding: theme.spacing(2),
        "& .MuiTypography-h6": {
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
        },
    },
    
    // ✅ CORREGIDO: DialogContent dinámico según tema
    dialogContent: {
        padding: theme.spacing(2),
        backgroundColor: theme.mode === 'dark' ? '#1f2937' : "#fafbfc",
    },
    
    // ✅ CORREGIDO: FormSection dinámico según tema
    formSection: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: "10px",
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
        boxShadow: theme.mode === 'dark' ? "0 1px 6px rgba(0,0,0,0.3)" : "0 1px 6px rgba(0,0,0,0.08)",
        border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e3f2fd'}`,
    },
    
    // ✅ CORREGIDO: SectionTitle dinámico según tema
    sectionTitle: {
        display: "flex",
        alignItems: "center",
        marginBottom: theme.spacing(1),
        color: theme.palette.primary.main,
        fontWeight: "bold",
        fontSize: "15px",
    },
    
    // ✅ CORREGIDO: FormField dinámico según tema
    formField: {
        marginBottom: theme.spacing(1.5),
        "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
            color: theme.palette.text.primary,
            "& fieldset": {
                borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
            },
            "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
            },
            "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
            },
        },
        "& .MuiInputLabel-root": {
            color: theme.palette.text.secondary || theme.palette.text.primary,
        },
        "& .MuiInputLabel-root.Mui-focused": {
            color: theme.palette.primary.main,
        },
        "& .MuiFormHelperText-root": {
            color: theme.palette.text.secondary || theme.palette.text.primary,
        },
    },
    
    multFieldLine: {
        display: "flex",
        gap: theme.spacing(1.5),
        "& > *": {
            flex: 1,
        },
    },
    
    // ✅ ESTILOS PARA BOTONES
    btnWrapper: {
        position: "relative",
    },
    
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    
    // ✅ CORREGIDO: CancelButton dinámico según tema
    cancelButton: {
        backgroundColor: theme.mode === 'dark' ? 'rgba(211, 47, 47, 0.2)' : "#ffebee",
        color: "#d32f2f",
        fontWeight: "bold",
        borderRadius: "8px",
        padding: "12px 24px",
        "&:hover": {
            backgroundColor: theme.mode === 'dark' ? 'rgba(211, 47, 47, 0.3)' : "#ffcdd2",
        },
    },
    
    saveButton: {
        background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
        fontWeight: "bold",
        borderRadius: "8px",
        padding: "12px 24px",
        boxShadow: "0 4px 15px rgba(25, 118, 210, 0.3)",
        "&:hover": {
            background: "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(25, 118, 210, 0.4)",
        },
    },
    
    // ✅ CORREGIDO: InfoBox dinámico según tema
    infoBox: {
        backgroundColor: theme.mode === 'dark' ? '#1e3a5f' : "#f0f8ff",
        border: `1px solid ${theme.mode === 'dark' ? '#2d4f7a' : '#e3f2fd'}`,
        borderRadius: "8px",
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
    },
    
    // ✅ CORREGIDO: InfoText dinámico según tema
    infoText: {
        color: theme.palette.primary.main,
        fontSize: "13px",
        marginBottom: theme.spacing(0.75),
    },
    
    // ✅ CORREGIDO: LinkText dinámico según tema
    linkText: {
        color: theme.palette.primary.main,
        textDecoration: "none",
        fontWeight: "bold",
        "&:hover": {
            textDecoration: "underline",
        },
    },
    
    // ✅ ESTILOS PARA CHIPS DE PROVEEDOR
    providerChip: {
        backgroundColor: "#e8f5e8",
        color: "#2e7d32",
        fontWeight: "bold",
        margin: "4px",
    },
    
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
    },
    
    // ✅ CORREGIDO: FormControl dinámico según tema
    formControlDynamic: {
        "& .MuiOutlinedInput-root": {
            backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
            color: theme.palette.text.primary,
            "& fieldset": {
                borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
            },
            "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
            },
            "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
            },
        },
        "& .MuiInputLabel-root": {
            color: theme.palette.text.secondary || theme.palette.text.primary,
        },
        "& .MuiMenuItem-root": {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            "&:hover": {
                backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#f5f5f5',
            },
        },
    },
    
    colorAdorment: {
        width: 20,
        height: 20,
    },
    
    // ✅ CORREGIDO: DialogActions dinámico según tema
    dialogActions: {
        padding: "16px 24px",
        backgroundColor: theme.mode === 'dark' ? '#1f2937' : "#f8f9fa",
        borderTop: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
    },
    
    // ✅ ESTILOS PARA EL MODAL AMPLIADO
    expandedPromptHelper: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: theme.spacing(0.5),
        width: "100%",
    },
    
    expandedPromptButton: {
        marginLeft: theme.spacing(1),
        textTransform: "none",
        fontSize: "12px",
    },
    
    expandedPromptDialog: {
        "& .MuiDialog-paper": {
            maxWidth: "90vw",
            width: "90vw",
            maxHeight: "90vh",
            height: "90vh",
        },
    },
    
    expandedPromptContent: {
        padding: theme.spacing(3),
        height: "calc(90vh - 120px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    },
    
    expandedPromptEditor: {
        flex: 1,
        marginTop: theme.spacing(2),
        minHeight: "300px",
        "& .MuiOutlinedInput-root": {
            height: "100%",
            alignItems: "flex-start",
            fontSize: "14px",
            lineHeight: "1.6",
            fontFamily: "'Courier New', monospace",
            backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#ffffff',
            color: theme.palette.text.primary,
            "& textarea": {
                height: "100% !important",
                overflow: "auto !important",
                padding: theme.spacing(2),
            },
        },
    },
    
    expandedPromptPreview: {
        flex: 1,
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#f9fafb',
        borderRadius: "8px",
        border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
        overflow: "auto",
        fontSize: "14px",
        lineHeight: "1.8",
        whiteSpace: "pre-wrap",
        wordWrap: "break-word",
        fontFamily: "'Courier New', monospace",
        minHeight: "200px",
    },
}));



// Definir initialState fuera del componente para evitar recreación
const initialState = {
    name: "",
    prompt: "",
    voice: "texto", // ✅ POR DEFECTO SIEMPRE "TEXTO"
    voiceKey: "", // ✅ COMENTADO EN UI PERO MANTENIDO INTERNAMENTE
    voiceRegion: "", // ✅ COMENTADO EN UI PERO MANTENIDO INTERNAMENTE
    maxTokens: 400, // ✅ OPTIMIZADO: De 100 a 400 (respuestas completas)
    temperature: 0.5, // ✅ OPTIMIZADO: De 1.0 a 0.5 (balanceado)
    apiKey: "",
    queueId: "", // ✅ CAMBIADO: De null a string vacío (OPCIONAL)
    maxMessages: 25, // ✅ OPTIMIZADO: De 10 a 25 (contexto adecuado)
    provider: "openrouter-deepseek",
    workMode: "layers" // ✅ NUEVO: Modo de trabajo (layers o pdfs)
};

// ✅ ESQUEMA DE VALIDACIÓN CON YUP
const validationSchema = Yup.object().shape({
    name: Yup.string().required("El nombre es obligatorio"),
    apiKey: Yup.string().required("La API Key es obligatoria"),
    prompt: Yup.string().required("El prompt es obligatorio"),
    maxTokens: Yup.number().min(1).max(4000).required("Los tokens máximos son obligatorios"),
    temperature: Yup.number().min(0).max(2).required("La temperatura es obligatoria"),
    maxMessages: Yup.number().min(1).max(100).required("Los mensajes máximos son obligatorios"),
    queueId: Yup.string().nullable().optional() // ✅ OPCIONAL
});

const PromptModal = ({ open, onClose, promptId, onSave }) => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);
    const [selectedVoice, setSelectedVoice] = useState("texto");
    const [showApiKey, setShowApiKey] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState("openrouter-deepseek");
    const [mediaFiles, setMediaFiles] = useState([]);
    const [loadingMedia, setLoadingMedia] = useState(false);
    const [urlModalOpen, setUrlModalOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(null);
    const fileInputRef = React.useRef(null);
    const [validatingApi, setValidatingApi] = useState(false);
    const [expandedPromptModalOpen, setExpandedPromptModalOpen] = useState(false);
    const [expandedPromptText, setExpandedPromptText] = useState("");
    const [expandedPromptChanged, setExpandedPromptChanged] = useState(false);
    const [apiValidated, setApiValidated] = useState(false);
    const [apiValidationError, setApiValidationError] = useState(null);
    // ✅ Estado para mensajes informativos en el modal (no toast)
    const [mediaMessage, setMediaMessage] = useState({ type: null, text: null }); // type: 'success' | 'error' | null
    // ✅ Estado para rastrear API Key y proveedor guardados (para comparar si cambiaron)
    const [savedApiKey, setSavedApiKey] = useState(null);
    const [savedProvider, setSavedProvider] = useState(null);
    const [selectedWorkMode, setSelectedWorkMode] = useState('layers'); // 'layers' o 'pdfs'

    const handleToggleApiKey = () => {
        setShowApiKey(!showApiKey);
    };

    const handleChangeProvider = (e) => {
        const newProvider = e.target.value;
        setSelectedProvider(newProvider);
        
        // ✅ Si cambió el proveedor, resetear validación
        if (newProvider !== savedProvider) {
            setApiValidated(false);
            setApiValidationError(null);
        } else if (newProvider === savedProvider && savedApiKey) {
            // ✅ Si volvió al proveedor original y la API Key no cambió, mantener validación
            setApiValidated(true);
            setApiValidationError(null);
        }
    };

    const [prompt, setPrompt] = useState(initialState);

    useEffect(() => {
        // ✅ Resetear estado cuando se abre el modal sin promptId
        if (!open) {
            return;
        }
        
        const fetchPrompt = async () => {
            if (!promptId) {
                console.log("[PromptModal] No hay promptId, reseteando a valores iniciales");
                setPrompt(initialState);
                setMediaFiles([]);
                setSelectedWorkMode(initialState.workMode || "layers"); // ✅ Resetear workMode al valor inicial
                return;
            }
            try {
                console.log("[PromptModal] Cargando prompt con ID:", promptId);
                const { data } = await api.get(`/prompt/${promptId}`);
                console.log("[PromptModal] Datos recibidos del backend:", {
                    id: data.id,
                    name: data.name,
                    workMode: data.workMode,
                    workModeType: typeof data.workMode,
                    workModeValue: data.workMode,
                    allData: data
                });
                
                setPrompt(prevState => {
                    // ✅ CONVERTIR queueId null A STRING VACÍO PARA EVITAR WARNINGS
                    const processedData = {
                        ...data,
                        queueId: data.queueId || "" // Si es null, undefined o 0, usar string vacío
                    };
                    return { ...prevState, ...processedData };
                });
                setSelectedVoice(data.voice);
                setSelectedProvider(data.provider || "openrouter-deepseek");
                
                // ✅ Cargar workMode del backend - corregido: data ya es el prompt completo
                // Asegurarse de que workMode sea válido ('layers' o 'pdfs')
                // Normalizar a minúsculas para evitar problemas de case sensitivity
                const normalizedWorkMode = String(data.workMode || "layers").toLowerCase();
                const workModeValue = (normalizedWorkMode === 'layers' || normalizedWorkMode === 'pdfs') 
                    ? normalizedWorkMode 
                    : "layers";
                console.log("[PromptModal] Estableciendo workMode:", workModeValue, "desde data.workMode:", data.workMode, "normalized:", normalizedWorkMode);
                setSelectedWorkMode(workModeValue);
                
                // ✅ Guardar API Key y proveedor originales para comparar cambios
                setSavedApiKey(data.apiKey);
                setSavedProvider(data.provider || "openrouter-deepseek");
                
                // ✅ Si el prompt ya existe y tiene API Key, marcar como validado automáticamente
                // (asumiendo que si está guardado, ya fue validado antes)
                if (data.apiKey && data.provider) {
                    setApiValidated(true);
                    setApiValidationError(null);
                }
                
                // ✅ Cargar archivos multimedia
                await fetchMediaFiles(promptId);
            } catch (err) {
                toastError(err);
            }
        };

        fetchPrompt();
    }, [promptId, open]); // Removido initialState de las dependencias

    // ✅ Función para cargar archivos multimedia (retorna el array directamente)
    const fetchMediaFiles = async (id) => {
        try {
            console.log("🔵 [fetchMediaFiles] Cargando archivos para promptId:", id);
            // NO cambiar loadingMedia aquí para evitar conflictos con otras operaciones
            const { data } = await api.get(`/prompt/${id}/media`);
            const files = data || [];
            
            // ✅ Limpiar y normalizar datos recibidos del backend
            const cleanedFiles = files.map(file => {
                // ✅ Limpiar mediaName: eliminar cualquier carácter no imprimible o espacios extra
                let cleanName = file.mediaName;
                if (cleanName) {
                    cleanName = String(cleanName)
                        .replace(/[\x00-\x1F\x7F-\x9F]/g, '') // Eliminar caracteres de control
                        .replace(/\0/g, '') // Eliminar null bytes
                        .trim();
                }
                
                // ✅ Limpiar mediaSize: asegurar que sea número
                let cleanSize = file.mediaSize;
                if (cleanSize) {
                    if (typeof cleanSize === 'string') {
                        cleanSize = parseInt(cleanSize.replace(/[^0-9]/g, ''), 10); // Solo números
                    }
                    cleanSize = Number(cleanSize);
                    if (isNaN(cleanSize)) cleanSize = null;
                }
                
                return {
                    ...file,
                    mediaName: cleanName || null,
                    mediaSize: cleanSize
                };
            });
            
            setMediaFiles(cleanedFiles);
            return cleanedFiles;
        } catch (err) {
            console.error("❌ [fetchMediaFiles] Error cargando archivos multimedia:", err);
            setMediaFiles([]);
            return []; // ✅ Retornar array vacío en caso de error
        }
    };

    // ✅ Función para subir archivo local (ahora soporta múltiples archivos)
    const handleFileUpload = async (files) => {
        console.log("🔵 [handleFileUpload] Iniciando upload, promptId:", promptId, "files:", files);
        console.log("🔵 [handleFileUpload] files.length:", files?.length, "files type:", typeof files);
        
        if (!promptId) {
            console.error("❌ [handleFileUpload] No hay promptId");
            setMediaMessage({ type: 'error', text: "Primero debes guardar el prompt" });
            return;
        }

        // ✅ CRÍTICO: Convertir FileList a Array INMEDIATAMENTE (antes de cualquier async)
        let filesArray = [];
        if (files && files.length > 0) {
            // Si es FileList, convertirlo a Array
            if (files instanceof FileList) {
                filesArray = Array.from(files);
            } else if (Array.isArray(files)) {
                filesArray = files;
            } else {
                console.error("❌ [handleFileUpload] Tipo de archivo no reconocido:", typeof files);
                setMediaMessage({ type: 'error', text: "Error: tipo de archivo no reconocido" });
                return;
            }
        } else {
            console.error("❌ [handleFileUpload] No hay archivos para subir");
            setMediaMessage({ type: 'error', text: "No se seleccionaron archivos" });
            return;
        }
        
        console.log("✅ [handleFileUpload] Archivos convertidos a Array:", filesArray.length);
        console.log("✅ [handleFileUpload] Nombres de archivos:", filesArray.map(f => f.name));

        try {
            // ✅ CRÍTICO: Recargar archivos desde el backend ANTES de validar (estado real)
            console.log("🔵 [handleFileUpload] Recargando archivos desde BD...");
            const currentFiles = await fetchMediaFiles(promptId);
            console.log("✅ [handleFileUpload] Archivos actuales en BD:", currentFiles.length);

        // ✅ Validar que no se excedan 6 archivos en total (usando valor retornado directamente)
        const totalAfterUpload = currentFiles.length + filesArray.length;
        if (totalAfterUpload > 6) {
            setMediaMessage({ 
                type: 'error', 
                text: `Máximo 6 archivos por prompt. Ya tienes ${currentFiles.length} y estás intentando agregar ${filesArray.length}. Solo puedes agregar ${6 - currentFiles.length} más.` 
            });
            return;
        }

        // Validar cada archivo
        const validFiles = [];
        const errors = [];

        for (const file of filesArray) {
            // Validar tamaño según tipo
            const fileType = file.type.split('/')[0];
            const maxSizes = {
                image: 10 * 1024 * 1024, // 10 MB
                video: 15 * 1024 * 1024, // 15 MB
                audio: 10 * 1024 * 1024, // 10 MB
                application: 15 * 1024 * 1024, // 15 MB (documentos)
            };

            const maxSize = maxSizes[fileType] || 15 * 1024 * 1024;
            if (file.size > maxSize) {
                const maxMB = maxSize / (1024 * 1024);
                errors.push(`${file.name}: excede ${maxMB}MB`);
                continue;
            }

            validFiles.push(file);
        }

        // Mostrar errores si hay
        if (errors.length > 0) {
            setMediaMessage({ 
                type: 'error', 
                text: `Algunos archivos no se pudieron agregar: ${errors.join(', ')}` 
            });
        }

        if (validFiles.length === 0) {
            return;
        }

            // Subir archivos uno por uno (recargando estado después de cada uno)
            setLoadingMedia(true);
            setMediaMessage({ type: null, text: null }); // Limpiar mensaje anterior
            const uploadedFiles = [];
            const failedFiles = [];

            console.log("🔵 [handleFileUpload] Iniciando subida de", validFiles.length, "archivos válidos");

            for (let i = 0; i < validFiles.length; i++) {
                const file = validFiles[i];
                console.log(`🔵 [handleFileUpload] Procesando archivo ${i + 1}/${validFiles.length}:`, file.name);
                
                // ✅ Recargar estado actual desde BD antes de cada validación
                const currentFiles = await fetchMediaFiles(promptId);
                
                // ✅ Validar usando el valor retornado directamente (no el estado)
                if (currentFiles.length >= 6) {
                    console.warn("⚠️ [handleFileUpload] Límite alcanzado:", currentFiles.length);
                    setMediaMessage({ 
                        type: 'error', 
                        text: `Máximo 6 archivos por prompt. Ya tienes ${currentFiles.length} archivos. No se pueden agregar más.` 
                    });
                    break;
                }

                const formData = new FormData();
                formData.append("file", file);
                
                try {
                    console.log(`🔵 [handleFileUpload] Enviando archivo ${file.name} al servidor...`);
                    const { data } = await api.post(`/prompt/${promptId}/media`, formData, {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    });
                    
                    console.log(`✅ [handleFileUpload] Archivo ${file.name} subido exitosamente:`, data);
                    uploadedFiles.push(data);
                    
                    // ✅ Recargar archivos desde BD después de cada éxito
                    await fetchMediaFiles(promptId);
                } catch (fileError) {
                    // Si falla un archivo, continuar con los demás
                    const errorMsg = fileError.response?.data?.message || fileError.message || "Error desconocido";
                    console.error(`❌ [handleFileUpload] Error subiendo ${file.name}:`, fileError);
                    failedFiles.push({ name: file.name, error: errorMsg });
                }
            }
            
            console.log("🔵 [handleFileUpload] Proceso completado. Exitosos:", uploadedFiles.length, "Fallidos:", failedFiles.length);
            
            // ✅ Mostrar mensajes en el modal
            if (uploadedFiles.length > 0 && failedFiles.length === 0) {
                setMediaMessage({ 
                    type: 'success', 
                    text: `${uploadedFiles.length} archivo(s) agregado(s) correctamente` 
                });
            } else if (uploadedFiles.length > 0 && failedFiles.length > 0) {
                setMediaMessage({ 
                    type: 'error', 
                    text: `${uploadedFiles.length} archivo(s) agregado(s), ${failedFiles.length} fallaron: ${failedFiles.map(f => f.name).join(', ')}` 
                });
            } else if (failedFiles.length > 0) {
                setMediaMessage({ 
                    type: 'error', 
                    text: `Error al subir archivos: ${failedFiles.map(f => `${f.name} (${f.error})`).join(', ')}` 
                });
            } else if (uploadedFiles.length === 0 && failedFiles.length === 0) {
                setMediaMessage({ 
                    type: 'error', 
                    text: `No se pudo subir ningún archivo. Verifica la consola para más detalles.` 
                });
            }
        } catch (err) {
            console.error("❌ [handleFileUpload] Error inesperado:", err);
            setMediaMessage({ 
                type: 'error', 
                text: `Error inesperado: ${err.message || "Error desconocido"}` 
            });
        } finally {
            setLoadingMedia(false);
            console.log("🔵 [handleFileUpload] Finalizado");
        }
    };

    // ✅ Función para eliminar archivo
    const handleDeleteMedia = async (mediaId) => {
        if (!promptId) return;
        
        try {
            setLoadingMedia(true);
            setMediaMessage({ type: null, text: null }); // Limpiar mensaje
            await api.delete(`/prompt/${promptId}/media/${mediaId}`);
            await fetchMediaFiles(promptId); // ✅ Recargar desde BD
            setMediaMessage({ type: 'success', text: "Archivo eliminado correctamente" });
        } catch (err) {
            setMediaMessage({ 
                type: 'error', 
                text: `Error al eliminar archivo: ${err.response?.data?.message || err.message || "Error desconocido"}` 
            });
        } finally {
            setLoadingMedia(false);
        }
    };

    // ✅ Función para agregar URL externa
    const handleAddUrl = async (url, caption) => {
        if (!promptId) {
            setMediaMessage({ type: 'error', text: "Primero debes guardar el prompt" });
            return;
        }

        // ✅ Recargar estado actual desde BD
        const currentFiles = await fetchMediaFiles(promptId);

        if (currentFiles.length >= 6) {
            setMediaMessage({ type: 'error', text: "Máximo 6 archivos por prompt" });
            return;
        }

        try {
            setLoadingMedia(true);
            setMediaMessage({ type: null, text: null }); // Limpiar mensaje
            const { data } = await api.post(`/prompt/${promptId}/media/url`, {
                mediaUrl: url,
                caption: caption || null,
            });
            await fetchMediaFiles(promptId); // ✅ Recargar desde BD
            setMediaMessage({ type: 'success', text: "URL externa agregada correctamente" });
            setUrlModalOpen(false);
        } catch (err) {
            setMediaMessage({ 
                type: 'error', 
                text: `Error al agregar URL: ${err.response?.data?.message || err.message || "Error desconocido"}` 
            });
        } finally {
            setLoadingMedia(false);
        }
    };

    // ✅ Función para reordenar (drag & drop básico)
    const handleReorder = (index, action) => {
        if (action === "start") {
            setIsDragging(index);
        } else if (action === "end") {
            setIsDragging(null);
        }
    };

      const handleClose = () => {
    setPrompt(initialState);
    setSelectedVoice("texto"); // ✅ SIEMPRE "TEXTO" POR DEFECTO
    setSelectedProvider("openrouter-deepseek");
    setSelectedWorkMode("layers"); // ✅ Resetear workMode al cerrar
    setMediaFiles([]);
    setUrlModalOpen(false);
    setIsDragging(null);
    setApiValidated(false);
    setApiValidationError(null);
    // ✅ Limpiar valores guardados al cerrar
    setSavedApiKey(null);
    setSavedProvider(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  // ✅ Función para validar API Key y proveedor
  const handleValidateApi = async (currentApiKey) => {
    // ✅ CRÍTICO: Usar SOLO el valor pasado como parámetro (valor actual del formulario)
    // NO usar prompt.apiKey (estado guardado) - esto asegura que validamos lo que el usuario escribió
    if (!currentApiKey || !currentApiKey.trim()) {
      toast.error("Por favor ingresa una API Key primero");
      return;
    }
    
    const apiKey = currentApiKey.trim();
    const provider = selectedProvider;

    if (!provider) {
      toast.error("Por favor selecciona un proveedor primero");
      return;
    }

    // ✅ Validar formato básico de API Key
    if (apiKey.length < 20) {
      toast.error("La API Key parece ser muy corta. Verifica que sea correcta.");
      setApiValidated(false);
      setApiValidationError("API Key muy corta");
      return;
    }

    try {
      setValidatingApi(true);
      setApiValidationError(null);
      setApiValidated(false);

      console.log(`[ValidateAPI] Validando API Key (primeros 10 chars): ${apiKey.substring(0, 10)}..., Proveedor: ${provider}`);
      console.log(`[ValidateAPI] Longitud de API Key: ${apiKey.length} caracteres`);

      const { data } = await api.post("/prompt/validate-api", {
        apiKey: apiKey, // ✅ Usar el valor exacto que el usuario ingresó
        provider: provider
      });

      if (data.success) {
        setApiValidated(true);
        setApiValidationError(null);
        toast.success(`✅ ${data.message || "Conexión exitosa"}`);
      } else {
        setApiValidated(false);
        setApiValidationError(data.message || "Error de validación");
        toast.error(data.message || "Error validando API");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || "Error validando API";
      setApiValidated(false);
      setApiValidationError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setValidatingApi(false);
    }
  };

  // ✅ ELIMINADA FUNCIÓN NO UTILIZADA: handleChangeVoice

  const handleSavePrompt = async values => {
        // ✅ VALIDAR API ANTES DE GUARDAR
        if (!apiValidated) {
            toast.error("⚠️ Debes validar la API Key y el proveedor antes de guardar. Usa el botón 'Probar Conexión'.");
            return;
        }

        const promptData = { ...values, voice: selectedVoice, provider: selectedProvider, workMode: selectedWorkMode };
        console.log("[PromptModal] Guardando prompt con workMode:", selectedWorkMode, "promptData:", promptData);
        // ✅ ELIMINADA VALIDACIÓN OBLIGATORIA: Permitir prompts sin departamento
        // if (!values.queueId) {
        //     toastError("Informe o setor");
        //     return;
        // }
        try {
            let savedPromptId = promptId;
            if (promptId) {
                await api.put(`/prompt/${promptId}`, promptData);
            } else {
                const { data } = await api.post("/prompt", promptData);
                savedPromptId = data.id;
            }
            
            // ✅ Actualizar API Key y proveedor guardados después de guardar exitosamente
            setSavedApiKey(values.apiKey);
            setSavedProvider(selectedProvider);
            
            toast.success(i18n.t("promptModal.success"));
            
            // ✅ Si es un prompt nuevo, recargar archivos multimedia
            if (!promptId && savedPromptId) {
                await fetchMediaFiles(savedPromptId);
            }
            
            // ✅ AGREGAR CALLBACK PARA REFRESCAR LISTA
            if (onSave) {
                onSave();
            }
        } catch (err) {
            toastError(err);
        }
        // ✅ NO cerrar el modal si es un prompt nuevo (para permitir agregar multimedia)
        if (promptId) {
        handleClose();
        }
    };

    return (
        <div className={classes.root}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                scroll="paper"
                fullWidth={false}
                PaperProps={{ style: { width: "840px", maxWidth: "90vw" } }}
            >
                <DialogTitle className={classes.dialogTitle}>
                    <Box display="flex" alignItems="center">
                        <Memory style={{ marginRight: 12, fontSize: 28 }} />
                        <Typography variant="h6">
                            {promptId
                                ? `${i18n.t("promptModal.title.edit")}`
                                : `${i18n.t("promptModal.title.add")}`}
                        </Typography>
                    </Box>
                </DialogTitle>
                
                <Formik
                    initialValues={prompt}
                    enableReinitialize={true}
                    validationSchema={validationSchema}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSavePrompt(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ isSubmitting, errors, touched, values, setFieldValue }) => {
                        // ✅ Guardar referencia a setFieldValue para usarla en el modal ampliado
                        window.setFieldValuePrompt = setFieldValue;
                        
                        return (
                        <Form style={{ width: "100%" }}>
                            <DialogContent className={classes.dialogContent}>
                                
                                {/* ✅ INFORMACIÓN SOBRE PROVEEDOR DE IA */}
                                <Paper className={classes.infoBox}>
                                    <Box display="flex" alignItems="center" marginBottom={1}>
                                        <Android style={{ marginRight: 8, color: "#1976d2" }} />
                                        <Typography variant="h6" style={{ color: "#1976d2", fontWeight: "bold" }}>
                                            <span role="img" aria-label="robot">🤖</span> Configuración de Proveedor de IA
                                        </Typography>
                                    </Box>
                                    <Typography className={classes.infoText}>
                                        Selecciona el proveedor de inteligencia artificial que utilizará este prompt. 
                                        Cada proveedor ofrece diferentes modelos y capacidades según tus necesidades.
                                    </Typography>
                                    <Typography className={classes.infoText}>
                                        <strong>Proveedores disponibles:</strong> OpenAI, Gemini, Grok, Deepseek y OpenRouter.
                                        Asegúrate de tener una API Key válida del proveedor seleccionado.
                                    </Typography>
                                </Paper>
                                
                                {/* ✅ SECCIÓN: CONFIGURACIÓN DE API */}
                                <Paper className={classes.formSection}>
                                    <Typography className={classes.sectionTitle}>
                                        <Security style={{ marginRight: 8 }} />
                                        Configuración de API
                                    </Typography>
                                    
                                    <FormControl fullWidth margin="dense" variant="outlined" className={classes.formField}>
                                        <TextField
                                            label={selectedProvider.startsWith('openai-oficial') ? "API Key de OpenAI" : "API Key de OpenRouter"}
                                            name="apiKey"
                                            value={values.apiKey || ""}
                                            onChange={(e) => {
                                                const newApiKey = e.target.value;
                                                setFieldValue('apiKey', newApiKey);
                                                
                                                // ✅ Solo resetear validación si realmente cambió la API Key
                                                if (newApiKey !== savedApiKey) {
                                                    setApiValidated(false);
                                                    setApiValidationError(null);
                                                } else if (newApiKey === savedApiKey && savedApiKey && selectedProvider === savedProvider) {
                                                    // ✅ Si volvió al valor original y el proveedor no cambió, mantener validación
                                                    setApiValidated(true);
                                                    setApiValidationError(null);
                                                }
                                            }}
                                            type={showApiKey ? 'text' : 'password'}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            error={(touched.apiKey && Boolean(errors.apiKey)) || (apiValidationError && !apiValidated)}
                                            helperText={
                                                (touched.apiKey && errors.apiKey) || 
                                                (apiValidationError && !apiValidated ? apiValidationError : "") ||
                                                (apiValidated ? "✅ API Key validada correctamente" : (selectedProvider.startsWith('openai-oficial') ? "Tu clave API de OpenAI para autenticación" : "Tu clave API de OpenRouter para autenticación"))
                                            }
                                            placeholder={selectedProvider.startsWith('openai-oficial') ? "sk-..." : "sk-or-v1-..."}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={handleToggleApiKey}>
                                                            {showApiKey ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </FormControl>
                                    
                                    <FormControl fullWidth margin="dense" variant="outlined" className={`${classes.formField} ${classes.formControlDynamic}`}>
                                        <InputLabel>Proveedor de IA</InputLabel>
                                        <Select
                                            id="provider-select"
                                            labelWidth={120}
                                            name="provider"
                                            value={selectedProvider}
                                            onChange={(e) => {
                                                handleChangeProvider(e);
                                                // Resetear validación si cambia el proveedor
                                                setApiValidated(false);
                                                setApiValidationError(null);
                                            }}
                                            multiple={false}
                                        >
                                            {/* ✅ OPENAI OFICIAL - PROVEEDORES DIRECTOS */}
                                            <MenuItem disabled style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', color: '#333' }}>
                                                OpenAI Oficial
                                            </MenuItem>
                                            <MenuItem key={"openai-oficial-gpt4"} value={"openai-oficial-gpt4"}>
                                                <Box display="flex" alignItems="center">
                                                    <Chip label="OpenAI" style={{ backgroundColor: '#10a37f', color: 'white', fontWeight: 'bold' }} size="small" />
                                                    <Typography style={{ marginLeft: 8 }}>OpenAI Oficial (GPT-4)</Typography>
                                                </Box>
                                            </MenuItem>
                                            <MenuItem key={"openai-oficial-gpt4-turbo"} value={"openai-oficial-gpt4-turbo"}>
                                                <Box display="flex" alignItems="center">
                                                    <Chip label="OpenAI" style={{ backgroundColor: '#10a37f', color: 'white', fontWeight: 'bold' }} size="small" />
                                                    <Typography style={{ marginLeft: 8 }}>OpenAI Oficial (GPT-4 Turbo - 128k tokens)</Typography>
                                                </Box>
                                            </MenuItem>
                                            <MenuItem key={"openai-oficial-gpt35"} value={"openai-oficial-gpt35"}>
                                                <Box display="flex" alignItems="center">
                                                    <Chip label="OpenAI" style={{ backgroundColor: '#10a37f', color: 'white', fontWeight: 'bold' }} size="small" />
                                                    <Typography style={{ marginLeft: 8 }}>OpenAI Oficial (GPT-3.5 Turbo - económico)</Typography>
                                                </Box>
                                            </MenuItem>
                                            
                                            {/* ✅ OPENROUTER - MÚLTIPLES PROVEEDORES */}
                                            <MenuItem disabled style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', color: '#333', marginTop: '8px' }}>
                                                OpenRouter (Múltiples modelos)
                                            </MenuItem>
                                            <MenuItem key={"openrouter-openai"} value={"openrouter-openai"}>
                                                <Box display="flex" alignItems="center">
                                                    <Chip label="OpenAI" className={classes.providerChip} size="small" />
                                                    <Typography style={{ marginLeft: 8 }}>OpenRouter (OpenAI GPT-3.5/4)</Typography>
                                                </Box>
                                            </MenuItem>
                                            <MenuItem key={"openrouter-deepseek"} value={"openrouter-deepseek"}>
                                                <Box display="flex" alignItems="center">
                                                    <Chip label="DeepSeek" className={classes.providerChip} size="small" />
                                                    <Typography style={{ marginLeft: 8 }}>OpenRouter (DeepSeek)</Typography>
                                                </Box>
                                            </MenuItem>
                                            <MenuItem key={"openrouter-meta"} value={"openrouter-meta"}>
                                                <Box display="flex" alignItems="center">
                                                    <Chip label="Meta" className={classes.providerChip} size="small" />
                                                    <Typography style={{ marginLeft: 8 }}>OpenRouter (Meta Llama 3.3 70B)</Typography>
                                                </Box>
                                            </MenuItem>
                                            <MenuItem key={"openrouter-gemini"} value={"openrouter-gemini"}>
                                                <Box display="flex" alignItems="center">
                                                    <Chip label="Gemini" className={classes.providerChip} size="small" />
                                                    <Typography style={{ marginLeft: 8 }}>OpenRouter (Google Gemini)</Typography>
                                                </Box>
                                            </MenuItem>
                                            <MenuItem key={"openrouter-qwen"} value={"openrouter-qwen"}>
                                                <Box display="flex" alignItems="center">
                                                    <Chip label="Qwen" className={classes.providerChip} size="small" />
                                                    <Typography style={{ marginLeft: 8 }}>OpenRouter (Qwen)</Typography>
                                                </Box>
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                    
                                    {/* ✅ Botón de validación debajo del Proveedor de IA */}
                                    <Box marginTop={1} display="flex" justifyContent="flex-end">
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            color={apiValidated ? "primary" : "default"}
                                            onClick={() => handleValidateApi(values.apiKey)}
                                            disabled={validatingApi || !values.apiKey || !selectedProvider}
                                            startIcon={validatingApi ? <CircularProgress size={16} /> : (apiValidated ? <Security style={{ color: "green" }} /> : <Security />)}
                                        >
                                            {validatingApi ? "Validando..." : apiValidated ? "✅ Validado" : "Probar Conexión"}
                                        </Button>
                                    </Box>
                                </Paper>
                                
                                {/* ✅ SECCIÓN: INFORMACIÓN BÁSICA */}
                                <Paper className={classes.formSection}>
                                    <Typography className={classes.sectionTitle}>
                                        <Memory style={{ marginRight: 8 }} />
                                        Información Básica
                                    </Typography>
                                    
                                    <TextField
                                        label="Nombre del Prompt"
                                        name="name"
                                        value={values.name || ""}
                                        onChange={(e) => setFieldValue('name', e.target.value)}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        className={classes.formField}
                                        error={touched.name && Boolean(errors.name)}
                                        helperText={(touched.name && errors.name) || "Ej: Bot de Soporte, Asistente de Ventas..."}
                                        placeholder="Ej: Bot de Soporte"
                                    />
                                    
                                    <TextField
                                        label="Prompt Principal"
                                        name="prompt"
                                        value={values.prompt || ""}
                                        onChange={(e) => setFieldValue('prompt', e.target.value)}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        className={classes.formField}
                                        rows={6}
                                        multiline={true}
                                        error={touched.prompt && Boolean(errors.prompt)}
                                        helperText={(touched.prompt && errors.prompt) || "Define cómo debe comportarse la IA. Sé específico y claro."}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        startIcon={<Fullscreen />}
                                                        className={classes.expandedPromptButton}
                                                        onClick={() => {
                                                            setExpandedPromptText(values.prompt || "");
                                                            setExpandedPromptChanged(false);
                                                            setExpandedPromptModalOpen(true);
                                                        }}
                                                        style={{ fontSize: '11px', padding: '4px 8px', marginRight: 8 }}
                                                    >
                                                        AMPLIAR
                                                    </Button>
                                                </InputAdornment>
                                            )
                                        }}
                                        placeholder="Eres un asistente de soporte técnico. Responde de manera profesional y útil..."
                                    />
                                    <Box display="flex" justifyContent="flex-end" marginTop={0.5}>
                                        <Typography variant="caption" color="textSecondary">
                                            Cantidad de caracteres: {(values.prompt || "").length}
                                        </Typography>
                                    </Box>
                                </Paper>
                                
                                {/* ✅ SECCIÓN: CONFIGURACIÓN AVANZADA */}
                                <Paper className={classes.formSection}>
                                    <Typography className={classes.sectionTitle}>
                                        <Settings style={{ marginRight: 8 }} />
                                        Configuración Avanzada
                                    </Typography>
                                    
                                    <div className={classes.multFieldLine}>
                                        <TextField
                                            label="Temperatura"
                                            name="temperature"
                                            value={values.temperature || ""}
                                            onChange={(e) => setFieldValue('temperature', e.target.value)}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            type="number"
                                            className={classes.formField}
                                            error={touched.temperature && Boolean(errors.temperature)}
                                            helperText={(touched.temperature && errors.temperature) || "0.0 = Determinístico, 2.0 = Muy creativo"}
                                            inputProps={{ min: 0, max: 2, step: 0.1 }}
                                        />
                                        <TextField
                                            label="Máximo Tokens"
                                            name="maxTokens"
                                            value={values.maxTokens || ""}
                                            onChange={(e) => setFieldValue('maxTokens', e.target.value)}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            type="number"
                                            className={classes.formField}
                                            error={touched.maxTokens && Boolean(errors.maxTokens)}
                                            helperText={(touched.maxTokens && errors.maxTokens) || "Límite de tokens en la respuesta"}
                                            inputProps={{ min: 1, max: 4000 }}
                                        />
                                        <TextField
                                            label="Máximo Mensajes"
                                            name="maxMessages"
                                            value={values.maxMessages || ""}
                                            onChange={(e) => setFieldValue('maxMessages', e.target.value)}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            type="number"
                                            className={classes.formField}
                                            error={touched.maxMessages && Boolean(errors.maxMessages)}
                                            helperText={(touched.maxMessages && errors.maxMessages) || "Mensajes en el historial"}
                                            inputProps={{ min: 1, max: 100 }}
                                        />
                                    </div>
                                    
                                    <FormControl fullWidth margin="dense" variant="outlined" className={`${classes.formField} ${classes.formControlDynamic}`}>
                                        <InputLabel>Departamentos (Opcional)</InputLabel>
                                        <Select
                                            label="Departamentos (Opcional)"
                                            name="queueId"
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            value={values.queueId || ""}
                                            onChange={(e) => setFieldValue("queueId", e.target.value)}
                                        >
                                            <MenuItem value="">
                                                <em>Sin departamento</em>
                                            </MenuItem>
                                            {Array.isArray(user?.queues) && user.queues.map((queue) => (
                                                <MenuItem key={queue.id} value={queue.id}>
                                                    {queue.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Paper>
                                
                                {/* ✅ SECCIÓN: ARCHIVOS MULTIMEDIA */}
                                <Paper className={classes.formSection}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
                                        <Typography className={classes.sectionTitle}>
                                            <AttachFile style={{ marginRight: 8 }} />
                                            Archivos Multimedia
                                        </Typography>
                                        <Box display="flex" gap={1}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<LinkIcon />}
                                                onClick={() => {
                                                    if (!promptId) {
                                                        toast.error("Primero debes guardar el prompt");
                                                        return;
                                                    }
                                                    setUrlModalOpen(true);
                                                }}
                                                disabled={loadingMedia || mediaFiles.length >= 6}
                                            >
                                                Agregar URL
                                            </Button>
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                multiple
                                                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                                                style={{ display: "none" }}
                                                onChange={(e) => {
                                                    const files = e.target.files;
                                                    console.log("🔵 [onChange] Archivos seleccionados:", files?.length);
                                                    
                                                    if (files && files.length > 0) {
                                                        // ✅ Convertir FileList a Array ANTES de pasar a la función
                                                        const filesArray = Array.from(files);
                                                        console.log("✅ [onChange] Archivos convertidos:", filesArray.length);
                                                        
                                                        // ✅ Limpiar el input DESPUÉS de procesar (no antes)
                                                        e.target.value = "";
                                                        
                                                        // ✅ Llamar a la función con el array
                                                        handleFileUpload(filesArray);
                                                    } else {
                                                        console.warn("⚠️ [onChange] No se seleccionaron archivos");
                                                    }
                                                }}
                                            />
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<CloudUpload />}
                                                onClick={() => {
                                                    if (!promptId) {
                                                        toast.error("Primero debes guardar el prompt");
                                                        return;
                                                    }
                                                    fileInputRef.current?.click();
                                                }}
                                                disabled={loadingMedia || mediaFiles.length >= 6}
                                            >
                                                Subir Archivo
                                            </Button>
                                        </Box>
                                    </Box>
                                    
                                    {/* ✅ Mensaje informativo/error en el modal (no toast) */}
                                    {mediaMessage.text && (
                                        <Box 
                                            style={{ 
                                                marginBottom: 16,
                                                padding: 12,
                                                borderRadius: 4,
                                                backgroundColor: mediaMessage.type === 'error' ? '#ffebee' : '#e8f5e9',
                                                border: `1px solid ${mediaMessage.type === 'error' ? '#f44336' : '#4caf50'}`,
                                                color: mediaMessage.type === 'error' ? '#c62828' : '#2e7d32'
                                            }}
                                        >
                                            <Typography style={{ fontSize: "13px", fontWeight: 500 }}>
                                                {mediaMessage.type === 'error' ? '❌' : '✅'} {mediaMessage.text}
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    <Box className={classes.infoBox} style={{ marginBottom: 16 }}>
                                        <Typography className={classes.infoText} style={{ fontSize: "12px" }}>
                                            📎 Puedes agregar hasta 6 archivos multimedia (imágenes, videos, audios, documentos)
                                        </Typography>
                                        <Typography className={classes.infoText} style={{ fontSize: "12px" }}>
                                            📏 Límites: Imágenes/Audios 10MB, Videos/Documentos 15MB
                                        </Typography>
                                        <Typography className={classes.infoText} style={{ fontSize: "12px" }}>
                                            💡 Usa la sintaxis <strong>[MEDIA: nombrearchivo.ext]</strong> en tu prompt para referenciar archivos
                                        </Typography>
                                    </Box>
                                    
                                    {loadingMedia ? (
                                        <Box display="flex" justifyContent="center" padding={3}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : (
                                        <PromptMediaPreview
                                            mediaFiles={mediaFiles}
                                            onDelete={handleDeleteMedia}
                                            onReorder={handleReorder}
                                            isDragging={isDragging}
                                        />
                                    )}
                                    
                                    {mediaFiles.length > 0 && (
                                        <Box marginTop={1}>
                                            <Typography variant="caption" color="textSecondary">
                                                {mediaFiles.length}/6 archivos agregados
                                            </Typography>
                                        </Box>
                                    )}
                                </Paper>
                                
                                {/* ✅ FASE 3: SECCIÓN: MODO DE TRABAJO */}
                                <Paper className={classes.formSection}>
                                    <Typography className={classes.sectionTitle}>
                                        <Settings style={{ marginRight: 8 }} />
                                        Modo de Trabajo
                                    </Typography>
                                    <FormControl fullWidth margin="dense" variant="outlined" className={`${classes.formField} ${classes.formControlDynamic}`}>
                                        <InputLabel>Modo de Trabajo</InputLabel>
                                        <Select
                                            key={`workmode-select-${promptId}-${selectedWorkMode}`}
                                            label="Modo de Trabajo"
                                            value={selectedWorkMode || "layers"}
                                            onChange={(e) => {
                                                console.log("[PromptModal] Cambiando workMode de", selectedWorkMode, "a", e.target.value);
                                                setSelectedWorkMode(e.target.value);
                                            }}
                                        >
                                            <MenuItem value="layers">
                                                <Box display="flex" alignItems="center">
                                                    <Layers style={{ marginRight: 8 }} />
                                                    <Typography>Capas Contextuales</Typography>
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="pdfs">
                                                <Box display="flex" alignItems="center">
                                                    <Description style={{ marginRight: 8 }} />
                                                    <Typography>Documentos PDF</Typography>
                                                </Box>
                                            </MenuItem>
                                        </Select>
                                        <FormHelperText>
                                            {selectedWorkMode === 'layers' 
                                                ? 'Usa capas contextuales activadas por keywords' 
                                                : 'Sube documentos PDF para que la IA aprenda de ellos'}
                                        </FormHelperText>
                                    </FormControl>
                                </Paper>
                                
                                {/* ✅ FASE 3: SECCIÓN CONDICIONAL: LAYERS O PDFs */}
                                {selectedWorkMode === 'layers' ? (
                                    <Paper className={classes.formSection}>
                                        <PromptLayersManager
                                            promptId={promptId}
                                            companyId={user?.companyId}
                                            onLayersChange={(layers) => {
                                                // Callback opcional para cuando cambien las layers
                                                console.log("Layers actualizadas:", layers.length);
                                            }}
                                        />
                                    </Paper>
                                ) : (
                                    <Paper className={classes.formSection}>
                                        <PromptDocumentsManager
                                            promptId={promptId}
                                            companyId={user?.companyId}
                                        />
                                    </Paper>
                                )}
                            </DialogContent>
                            
                            <DialogActions className={classes.dialogActions}>
                                <Button
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                    className={classes.cancelButton}
                                >
                                    CANCELAR
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={classes.saveButton}
                                >
                                    {promptId
                                        ? `${i18n.t("promptModal.buttons.okEdit")}`
                                        : `${i18n.t("promptModal.buttons.okAdd")}`}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                        );
                    }}
                </Formik>
            </Dialog>
            
            {/* ✅ MODAL PARA AGREGAR URL EXTERNA */}
            <Dialog
                open={urlModalOpen}
                onClose={() => setUrlModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle className={classes.dialogTitle}>
                    <Box display="flex" alignItems="center">
                        <LinkIcon style={{ marginRight: 12, fontSize: 28 }} />
                        <Typography variant="h6">
                            Agregar URL Externa
                        </Typography>
                    </Box>
                </DialogTitle>
                <UrlModalContent
                    onAdd={handleAddUrl}
                    onClose={() => setUrlModalOpen(false)}
                    loading={loadingMedia}
                    classes={classes}
                />
            </Dialog>
            
            {/* ✅ MODAL AMPLIADO PARA EDITAR PROMPT */}
            {expandedPromptModalOpen && (
                <ExpandedPromptModal
                    open={expandedPromptModalOpen}
                    promptText={expandedPromptText}
                    onClose={() => {
                        if (expandedPromptChanged) {
                            if (window.confirm("¿Deseas guardar los cambios antes de cerrar?")) {
                                handleSaveExpandedPrompt();
                            } else {
                                setExpandedPromptModalOpen(false);
                                setExpandedPromptChanged(false);
                            }
                        } else {
                            setExpandedPromptModalOpen(false);
                        }
                    }}
                    onSave={(newText) => {
                        // Actualizar el campo del formulario usando setFieldValue de Formik
                        // Necesitamos pasar setFieldValue desde el render de Formik
                        handleSaveExpandedPrompt(newText);
                    }}
                    onChange={(newText) => {
                        setExpandedPromptText(newText);
                        setExpandedPromptChanged(true);
                    }}
                    hasChanges={expandedPromptChanged}
                    classes={classes}
                />
            )}
        </div>
    );
    
    // ✅ FUNCIÓN PARA GUARDAR CAMBIOS DEL MODAL AMPLIADO
    function handleSaveExpandedPrompt(newText = null) {
        const textToSave = newText || expandedPromptText;
        // Usar setFieldValue de Formik si está disponible
        if (window.setFieldValuePrompt) {
            window.setFieldValuePrompt('prompt', textToSave);
        } else {
            // Fallback: actualizar directamente el campo
            const formikForm = document.querySelector('form');
            if (formikForm) {
                const promptInput = formikForm.querySelector('textarea[name="prompt"]');
                if (promptInput) {
                    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                        window.HTMLTextAreaElement.prototype,
                        "value"
                    )?.set;
                    if (nativeInputValueSetter) {
                        nativeInputValueSetter.call(promptInput, textToSave);
                    }
                    const event = new Event('input', { bubbles: true });
                    promptInput.dispatchEvent(event);
                }
            }
        }
        setExpandedPromptModalOpen(false);
        setExpandedPromptChanged(false);
        toast.success("Prompt actualizado correctamente");
    }
};

// ✅ COMPONENTE SEPARADO PARA EL MODAL AMPLIADO DE PROMPT
const ExpandedPromptModal = ({ open, promptText, onClose, onSave, onChange, hasChanges, classes }) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            className={classes.expandedPromptDialog}
            maxWidth={false}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">
                        Editor Ampliado - Prompt Principal
                    </Typography>
                    {hasChanges && (
                        <Chip 
                            label="Cambios sin guardar" 
                            style={{ backgroundColor: '#ff9800', color: '#fff' }}
                            size="small"
                        />
                    )}
                </Box>
            </DialogTitle>
            <DialogContent className={classes.expandedPromptContent}>
                <Box display="flex" flexDirection="column" height="100%">
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        Edita tu prompt con más espacio. Las referencias a archivos multimedia <code>[MEDIA: nombrearchivo.ext]</code> aparecerán resaltadas en la vista previa.
                    </Typography>
                    
                    <TextField
                        multiline
                        fullWidth
                        variant="outlined"
                        value={promptText}
                        onChange={(e) => onChange(e.target.value)}
                        className={classes.expandedPromptEditor}
                        placeholder="Escribe tu prompt aquí..."
                        InputProps={{
                            style: {
                                height: "100%",
                            },
                        }}
                    />
                    
                    <Box display="flex" justifyContent="flex-end" marginTop={1}>
                        <Typography variant="body2" color="textSecondary" style={{ fontWeight: 500 }}>
                            Cantidad de caracteres: {promptText.length}
                        </Typography>
                    </Box>
                    
                    <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
                        Vista Previa (con resaltado):
                    </Typography>
                    
                    <Box 
                        className={classes.expandedPromptPreview}
                        dangerouslySetInnerHTML={{
                            __html: formatPromptPreview(promptText)
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button
                    onClick={onClose}
                    className={classes.cancelButton}
                >
                    Cancelar
                </Button>
                <Button
                    onClick={() => onSave(promptText)}
                    className={classes.saveButton}
                    disabled={!hasChanges}
                >
                    Guardar Cambios
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ✅ FUNCIÓN PARA FORMATEAR LA VISTA PREVIA CON RESALTADO
const formatPromptPreview = (text) => {
    if (!text) return "";
    
    // Escapar HTML primero
    let html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');
    
    // Resaltar sintaxis [MEDIA: nombrearchivo.ext]
    html = html.replace(
        /\[MEDIA:\s*([^\]]+)\]/gi,
        '<span style="background-color: #dbeafe; color: #1e40af; padding: 2px 6px; border-radius: 4px; font-weight: bold; font-family: \'Courier New\', monospace;">[MEDIA: $1]</span>'
    );
    
    // Resaltar secciones (líneas que empiezan con ** o #)
    html = html.replace(
        /(\*\*[^*]+\*\*|#{1,6}\s+[^\n]+)/g,
        '<span style="background-color: #d1fae5; color: #065f46; padding: 4px 8px; border-radius: 4px; font-weight: bold; display: inline-block; margin-bottom: 4px;">$1</span>'
    );
    
    return html;
};

// ✅ COMPONENTE SEPARADO PARA EL MODAL DE URL
const UrlModalContent = ({ onAdd, onClose, loading, classes }) => {
    const [url, setUrl] = React.useState("");
    const [caption, setCaption] = React.useState("");

    const handleSubmit = () => {
        if (url && url.trim()) {
            onAdd(url.trim(), caption.trim() || null);
            setUrl("");
            setCaption("");
        }
    };

    return (
        <>
            <DialogContent className={classes.dialogContent}>
                <TextField
                    label="URL del Archivo"
                    variant="outlined"
                    fullWidth
                    margin="dense"
                    className={classes.formField}
                    placeholder="https://ejemplo.com/archivo.jpg"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onKeyPress={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSubmit();
                        }
                    }}
                />
                <TextField
                    label="Descripción (Opcional)"
                    variant="outlined"
                    fullWidth
                    margin="dense"
                    className={classes.formField}
                    placeholder="Descripción del archivo"
                    multiline
                    rows={2}
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                />
                <Box className={classes.infoBox} style={{ marginTop: 16 }}>
                    <Typography className={classes.infoText} style={{ fontSize: "12px" }}>
                        💡 Asegúrate de que la URL sea accesible públicamente
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button
                    onClick={onClose}
                    className={classes.cancelButton}
                >
                    Cancelar
                </Button>
                <Button
                    onClick={handleSubmit}
                    className={classes.saveButton}
                    disabled={loading || !url.trim()}
                >
                    Agregar
                </Button>
            </DialogActions>
        </>
    );
};

export default PromptModal;